<template>
  <section class="feedback-history-page">
    <b-container class="py-4">
      <div class="history-header d-flex align-items-center mb-1">
        <a class="back-link me-3" @click="$router.back()">&larr; Back</a>
        <h1 class="history-title mb-0">My Essays</h1>
      </div>
      <p class="history-subtitle mb-4">Your drafts and submitted essays, newest first.</p>

      <div v-if="loading" class="text-center py-5">
        <b-spinner label="Loading submissions..."></b-spinner>
      </div>

      <b-alert v-else-if="error" show variant="danger">
        Could not load your submissions. Please refresh and try again.
      </b-alert>

      <div v-else-if="submissions.length === 0" class="empty-state py-5 text-center">
        <p class="text-muted">No essays yet. Start writing to save a draft automatically.</p>
        <b-button variant="primary" @click="$router.push({ name: 'SelectModule' })">
          Start an Essay
        </b-button>
      </div>

      <div v-else>
        <b-card
          v-for="submission in submissions"
          :key="submission.id"
          class="submission-card mb-3"
          @click="goToDetail(submission.id)"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div class="submission-info">
              <p class="prompt-preview mb-1">
                {{ truncatePrompt(submission.promptText) }}
              </p>
              <small class="submission-meta">
                {{ formatDate(submission.createdAt) }}
                &bull; {{ submission.moduleVersion || "—" }}
              </small>
            </div>
            <span :class="['status-badge', `status-${submission.status}`]">
              {{ statusLabel(submission.status) }}
            </span>
          </div>
        </b-card>
      </div>
    </b-container>
  </section>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { subscribeToEssaySubmissions } from "@/services/essaySubmissions";

export default {
  name: "FeedbackHistoryView",
  setup() {
    const router = useRouter();
    const submissions = ref([]);
    const loading = ref(true);
    const error = ref(false);
    let unsubscribe = null;

    onMounted(() => {
      unsubscribe = subscribeToEssaySubmissions(
        data => {
          submissions.value = data;
          loading.value = false;
        },
        err => {
          console.error("Failed to load submissions:", err);
          error.value = true;
          loading.value = false;
        }
      );
    });

    onBeforeUnmount(() => {
      if (unsubscribe) unsubscribe();
    });

    const goToDetail = id => {
      router.push({ name: "EssayDetail", params: { submissionId: id } });
    };

    const truncatePrompt = text => {
      if (!text) return "No prompt recorded";
      return text.length > 120 ? text.slice(0, 120) + "…" : text;
    };

    const formatDate = ts => {
      if (!ts) return "—";
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    };

    const statusLabel = status => {
      if (status === "draft") return "Draft";
      if (status === "complete") return "Complete";
      if (status === "error") return "Error";
      return "Pending";
    };

    return { submissions, loading, error, goToDetail, truncatePrompt, formatDate, statusLabel };
  }
};
</script>

<style scoped>
.feedback-history-page {
  min-height: 100vh;
  background: radial-gradient(1200px 500px at 20% -10%, #f2f1ff 0%, #f8f8fc 45%, #f6f7fb 100%);
}

.history-header {
  margin-top: 10px;
}

.history-title {
  font-size: clamp(2rem, 2.4vw, 2.9rem);
  font-weight: 700;
  line-height: 1.1;
  color: #1f2430;
}

.history-subtitle {
  margin-top: 10px;
  color: #6e7785;
  font-size: 1.08rem;
  font-weight: 500;
}

.back-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #5d40d6;
  font-size: 1.05rem;
  font-weight: 600;
  text-decoration: none;
  background: #ede8ff;
  border: 1px solid #d8cdfd;
  border-radius: 999px;
  min-height: 36px;
  padding: 0 14px;
  transition: all 0.18s ease;
}

.back-link:hover {
  color: #4a31bb;
  background: #e4ddff;
  border-color: #c7b8fb;
}

.submission-card {
  cursor: pointer;
  border: 1px solid #dde1ea;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 12px rgba(40, 47, 66, 0.06);
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}

.submission-card:hover {
  transform: translateY(-1px);
  border-color: #cfd5e4;
  box-shadow: 0 10px 24px rgba(32, 41, 61, 0.12);
}

.prompt-preview {
  font-weight: 600;
  font-size: 1.03rem;
  line-height: 1.35;
  color: #23283a;
}

.submission-meta {
  color: #6e7785;
  font-size: 0.95rem;
  font-weight: 500;
}

.status-badge {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  padding: 6px 12px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
}

.status-complete {
  background: #d4edda;
  color: #155724;
}

.status-pending {
  background: #fff3cd;
  color: #856404;
}

.status-error {
  background: #f8d7da;
  color: #721c24;
}

.status-draft {
  background: #e8e8f0;
  color: #4a4a6a;
}

@media (max-width: 768px) {
  .history-header {
    align-items: flex-start !important;
    flex-direction: column;
    gap: 12px;
  }

  .history-title {
    font-size: 2rem;
  }

  .history-subtitle {
    font-size: 0.98rem;
  }

  .prompt-preview {
    font-size: 0.98rem;
  }

  .submission-meta {
    font-size: 0.88rem;
  }
}
</style>
